import { theme } from '@antv/g2plot/lib/adaptor/common';

import {
  AIResourceTypes,
  Prompt,
  PromptInput,
  PromptMessageRole,
  promptModels,
  PromptOutput,
} from '../../../../services/backendService/types';
import {
  MAX_LIMIT_UPLOADEDTEXT,
  validPassagesTypes,
  validSlidesFileTypes,
} from '../../../../utils/aiGeneratorUtils';
import { generateUniqueId } from '../../../../utils/general';
import { createSimpleSlateParagraphAsText } from '../../../SlideComponents/slateUtil';

export type NewPromptTemplate =
  | 'brainBreak'
  | 'wouldYouRather'
  | 'empty'
  | 'negativePrompt';
export const createPromptUniqueId = () => `prompt-${generateUniqueId()}`;
export const createOutputUniqueId = () => `output-${generateUniqueId()}`;
export const createNewPrompt = (
  id: string,
  name: string,
  newPromptTemplate?: NewPromptTemplate,
): Prompt => {
  switch (newPromptTemplate) {
    case 'brainBreak':
      return {
        id,
        name,
        messages: [
          {
            role: 'system',
            content:
              '[{"type":"paragraph","children":[{"text":"You are a teaching assistant that create fun drawing activities for "},{"type":"promptInput","inputId":"input-grade","label":"Grade","children":[{"text":""}]},{"text":" students"}]}]',
          },
          {
            role: 'user',
            content:
              '[{"type":"paragraph","children":[{"text":"Create a short funny drawing activity about the topic "},{"type":"promptInput","inputId":"input-topic","label":"Topic","children":[{"text":""}]},{"text":". You should not use more than 20 words"}]}]',
          },
        ],
        promptOutput: {
          type: 'string',
          description: '',
          properties: {},
          resource: { type: 'openAi', model: promptModels.gpt4o },
          propertiesOrder: undefined,
        },
      };
    case 'wouldYouRather':
      const id1 = createOutputUniqueId();
      const id2 = createOutputUniqueId();
      return {
        id,
        name,
        messages: [
          {
            role: 'system',
            content:
              '[{"type":"paragraph","children":[{"text":"You are generating consequential dilemma questions with 2 possible options. It should encourage people to think creatively."}]}]',
          },
          {
            role: 'user',
            content:
              '[{"type":"paragraph","children":[{"text":"Create a dilemma question about topic "},{"type":"promptInput","inputId":"input-topic","label":"Topic","children":[{"text":""}]},{"text":""}]}]',
          },
        ],
        promptOutput: {
          type: 'object',
          description: '',
          properties: {
            [id1]: {
              id: id1,
              type: 'string',
              description: 'The dilemma question',
            },
            [id2]: {
              id: id2,
              type: 'array',
              items: {
                maxLength: 2,
                type: 'string',
              },
              description: 'the options to be answered',
            },
          },
          propertiesOrder: [id1, id2],
          resource: { type: 'openAi', model: promptModels.gpt4o },
        },
      };
  }
  return {
    id,
    name,
    messages: [
      {
        role: 'system',
        content: createSimpleSlateParagraphAsText('You are a teaching assistant'),
      },
      {
        role: 'user',
        content: createSimpleSlateParagraphAsText(''),
      },
    ],
    promptOutput: {
      type: 'string',
      description: '',
      properties: {},
      propertiesOrder: undefined,
      resource: { type: 'openAi', model: promptModels.gpt4o },
    },
  };
};

export const createNewImagePrompt = (
  id: string,
  name: string,
  newPromptTemplate?: NewPromptTemplate,
): Prompt => {
  switch (newPromptTemplate) {
    case 'negativePrompt':
      return {
        id,
        name,
        messages: [
          {
            role: 'user',
            content: createSimpleSlateParagraphAsText('Painting of George Washington'),
          },
        ],
        negativePrompt:
          '[{"type":"paragraph","children":[{"text":"((((ugly)))), (((duplicate))), ((morbid)), ((mutilated)), out of frame, extra fingers, mutated hands, ((poorly drawn hands)), ((poorly drawn face)), (((mutation))), (((deformed))), ((ugly)), blurry, ((bad anatomy)), (((bad proportions))), ((extra limbs)), cloned face, (((disfigured))), out of frame, ugly, extra limbs, (bad anatomy), gross proportions, (malformed limbs), ((missing arms)), ((missing legs)), (((extra arms))), (((extra legs))), mutated hands, (fused fingers), (too many fingers), (((long neck))), ((((ugly)))), (((duplicate))), ((morbid)), ((mutilated)), out of frame, extra fingers, mutated hands, ((poorly drawn hands)), ((poorly drawn face)), (((mutation))), (((deformed))), ((ugly)), blurry, ((bad anatomy)), (((bad proportions))), ((extra limbs)), cloned face, (((disfigured))), out of frame, ugly, extra limbs, (bad anatomy), gross proportions, (malformed limbs), ((missing arms)), ((missing legs)), (((extra arms))), (((extra legs))), mutated hands, (fused fingers), (too many fingers), (((long neck))) , ((((ugly)))), (((duplicate))), ((morbid)), ((mutilated)), out of frame, extra fingers, mutated hands, ((poorly drawn hands)), ((poorly drawn face)), (((mutation))), (((deformed))), ((ugly)), blurry, ((bad anatomy)), (((bad proportions))), ((extra limbs)), cloned face, (((disfigured))), out of frame, ugly, extra limbs, (bad anatomy), gross proportions, (malformed limbs), ((missing arms)), ((missing legs)), (((extra arms))), (((extra legs))), mutated hands, (fused fingers), (too many fingers), (((long neck)))","color":"#282F33"}],"align":"center","size":80}]',
        promptOutput: {
          type: 'string',
          description: '',
          properties: {},
          propertiesOrder: undefined,
          resource: {
            type: AIResourceTypes.stableDiffusionImage,
            model: promptModels.sd3,
          },
        },
      };
  }
  return {
    id,
    name,

    messages: [
      {
        role: 'user',
        content: createSimpleSlateParagraphAsText(''),
      },
    ],
    promptOutput: {
      type: 'string',
      description: '',
      properties: {},
      propertiesOrder: undefined,
      resource: {
        type: AIResourceTypes.stableDiffusionImage,
        model: promptModels.sd3Turbo,
      },
    },
  };
};

export const createSlidesUploadPromptInput = ({
  id,
  label,
}: {
  id?: string;
  label?: string;
}): PromptInput => {
  return {
    id: id ? id : generateUniqueId().toString(),
    label: label ? label : 'Upload slides',
    type: 'slidesUpload',
    order: 0,
    value: undefined,
    constraints: {
      allowedFileTypes: validSlidesFileTypes,
      step: 10, // used to determine the amount of placeholder slides to create during design process
    },
    allowEmpty: false,
    isRequired: true,
  };
};

export const createGradeDropdownPromptInput = () => {
  const gradePrompt: PromptInput = {
    id: `input-grade`,
    label: 'Grade',
    order: 1,
    value: '7th',
    constraints: {
      options: [
        { displayName: '1st', value: '1st' },
        { displayName: '2nd', value: '2nd' },
        { displayName: '3rd', value: '3rd' },
        { displayName: '4th', value: '4th' },
        { displayName: '5th', value: '5th' },
        { displayName: '6th', value: '6th' },
        { displayName: '7th', value: '7th' },
        { displayName: '8th', value: '8th' },
        { displayName: '9th', value: '9th' },
        { displayName: '10th', value: '10th' },
        { displayName: '11th', value: '11th' },
        { displayName: '12th', value: '12th' },
        { displayName: 'Professional development', value: 'Professional development' },
      ],
    },
    defaultValue: '7th',
    type: 'dropdown',
    allowEmpty: false,
    isRequired: true,
  };
  return gradePrompt;
};

export const createExtraLongTextPromptInput = (label?: string): PromptInput => {
  return {
    id: `input-extra-long-text`,
    label: label ? label : 'Text',
    order: 0,
    value: '',
    constraints: {
      maxLength: MAX_LIMIT_UPLOADEDTEXT,
    },
    type: 'longText',
    allowEmpty: false,
    isRequired: true,
  };
};

export const createPassageInput = (label?: string): PromptInput => {
  return {
    id: `input-passage-long-text`,
    label: label ? label : 'Text',
    order: 0,
    value: '',
    constraints: {
      maxLength: MAX_LIMIT_UPLOADEDTEXT,
      allowedFileTypes: validPassagesTypes, //used to determine if this is a passage type
    },
    type: 'passage',
    allowEmpty: false,
    isRequired: true,
  };
};

export type AiFeedbackType =
  | 'elaShortAnswer'
  | 'scienceShortAnswer'
  | 'socialStudiesShortAnswer'
  | 'custom';
export type AiDrawingFeedbackType = 'custom' | 'science' | 'socialStudies' | 'math';

export const createAiFeedbackPrompt = (type?: AiFeedbackType): Prompt => {
  if (type === 'custom') {
    const systemInstruction = {
      content: JSON.stringify([
        {
          type: 'paragraph',
          children: [
            {
              text: '# Role: An expert ',
            },
            {
              type: 'livePromptInput',
              inputId: 'subject',
              label: 'Subject',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: ' teacher ',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'You are giving a student very short and concise feedback on their written work for a given task in an interactive lesson.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          children: [
            {
              text: '## Communication policy.',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '- You always respond in ',
            },
            {
              type: 'livePromptInput',
              inputId: 'language',
              label: 'Language',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '- You always respond to the student with text written at a ',
            },
            {
              type: 'livePromptInput',
              inputId: 'grade',
              label: 'Grade',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: ' grade Flesch-Kincaid readability level.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '## The current lesson',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: 'Below is all content provided in the lesson.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '<Lesson>',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
            {
              type: 'livePromptInput',
              inputId: 'lessonContext',
              label: 'Lesson Context',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '</Lesson>',
            },
          ],
        },
      ]),

      role: 'system' as PromptMessageRole,
    };
    const promptInstruction = {
      content: JSON.stringify([
        {
          type: 'paragraph',
          children: [
            {
              text: 'The student has been working on the task provided on the slide.',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '<Slide>',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
            {
              type: 'livePromptInput',
              inputId: 'slideContext',
              label: 'Slide Context',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '</Slide>',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: "Here is the students' answer: ",
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '---',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
            {
              type: 'livePromptInput',
              inputId: 'slide-question-answer',
              label: 'Student response',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '---',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: 'First write something nice, then use this as a rubric:',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Content: The response demonstrates a clear understanding of the prompt and provides relevant and accurate information.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Evidence: The response includes specific and appropriate evidence from the text to support the answer.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: 'Organization: The response is well-organized and easy to follow.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Language: The response uses appropriate language and grammar, and is free of spelling and punctuation errors.',
            },
          ],
        },
      ]),
      role: 'user' as PromptMessageRole,
    };
    const promptOutput: PromptOutput = {
      type: 'string',
      description: 'feedback for student',
      properties: {},
      resource: { type: 'openAi', model: promptModels.gpt4o },
      propertiesOrder: undefined,
    };
    return {
      id: 'prompt',
      name: 'Prompt',
      messages: [systemInstruction, promptInstruction],
      promptOutput,
    };
  }

  if (type === 'scienceShortAnswer') {
    const systemInstruction = {
      content: JSON.stringify([
        {
          type: 'paragraph',
          children: [
            {
              text: '# Role: An expert ',
            },
            {
              type: 'livePromptInput',
              inputId: 'subject',
              label: 'Subject',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: ' teacher ',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'You are giving a student very short and concise feedback on their written work for a given task in an interactive lesson.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          children: [
            {
              text: '## Communication policy.',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '- You always respond in ',
            },
            {
              type: 'livePromptInput',
              inputId: 'language',
              label: 'Language',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '- You always respond to the student with text written at a ',
            },
            {
              type: 'livePromptInput',
              inputId: 'grade',
              label: 'Grade',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: ' grade Flesch-Kincaid readability level.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '## The current lesson',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: 'Below is all content provided in the lesson.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '<Lesson>',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
            {
              type: 'livePromptInput',
              inputId: 'lessonContext',
              label: 'Lesson Context',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '</Lesson>',
            },
          ],
        },
      ]),
      role: 'system' as PromptMessageRole,
    };
    const promptInstruction = {
      content: JSON.stringify([
        {
          type: 'paragraph',
          children: [
            {
              text:
                'The student has been working on the science task provided on the slide.',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '<Slide>' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            { text: '' },
            {
              type: 'livePromptInput',
              inputId: 'slideContext',
              label: 'Slide Context',
              children: [{ text: '' }],
            },
            { text: '' },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '</Slide>' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: "Here is the students' answer: " }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '---' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            { text: '' },
            {
              type: 'livePromptInput',
              inputId: 'slide-question-answer',
              label: 'Student response',
              children: [{ text: '' }],
            },
            { text: '' },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '---' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: 'First write something nice, then use this as a rubric:' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Content knowledge: Demonstrates a thorough and accurate understanding of the topic, providing specific and relevant details.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Clarity of response: resents a clear and concise response with well-organized ideas.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Use of evidence: Integrates specific and relevant evidence seamlessly to support the response.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Critical thinking: Demonstrates insightful and nuanced thinking, connecting ideas with depth.',
            },
          ],
        },
      ]),
      role: 'system' as PromptMessageRole,
    };

    const promptOutput: PromptOutput = {
      type: 'string',
      description: 'feedback for student',
      properties: {},
      propertiesOrder: undefined,
      resource: { type: 'openAi', model: promptModels.gpt4o },
    };
    return {
      id: 'prompt',
      name: 'Prompt',
      messages: [systemInstruction, promptInstruction],
      promptOutput,
    };
  }
  if (type === 'socialStudiesShortAnswer') {
    const systemInstruction = {
      content: JSON.stringify([
        {
          type: 'paragraph',
          children: [
            {
              text: '# Role: An expert ',
            },
            {
              type: 'livePromptInput',
              inputId: 'subject',
              label: 'Subject',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: ' teacher ',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'You are giving a student very short and concise feedback on their written work for a given task in an interactive lesson.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          children: [
            {
              text: '## Communication policy.',
            },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '- You always respond in ',
            },
            {
              type: 'livePromptInput',
              inputId: 'language',
              label: 'Language',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '- You always respond to the student with text written at a ',
            },
            {
              type: 'livePromptInput',
              inputId: 'grade',
              label: 'Grade',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: ' grade Flesch-Kincaid readability level.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '## The current lesson',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: 'Below is all content provided in the lesson.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '<Lesson>',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '',
            },
            {
              type: 'livePromptInput',
              inputId: 'lessonContext',
              label: 'Lesson Context',
              children: [
                {
                  text: '',
                },
              ],
            },
            {
              text: '',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text: '</Lesson>',
            },
          ],
        },
      ]),
      role: 'system' as PromptMessageRole,
    };
    const promptInstruction = {
      content: JSON.stringify([
        {
          type: 'paragraph',
          children: [
            { text: 'The student has been working on the task provided on the slide.' },
          ],
          align: 'center',
          size: 80,
          color: '#282F33',
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '<Slide>' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            { text: '' },
            {
              type: 'livePromptInput',
              inputId: 'slideContext',
              label: 'Slide Context',
              children: [{ text: '' }],
            },
            { text: '' },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '</Slide>' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: "Here is the students' answer: " }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '---' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            { text: '' },
            {
              type: 'livePromptInput',
              inputId: 'slide-question-answer',
              label: 'Student response',
              children: [{ text: '' }],
            },
            { text: '' },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '---' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: 'First write something nice, then use this as a rubric:' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Content knowledge: Demonstrates a thorough and accurate understanding of the topic, providing specific and relevant details.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Clarity of response: resents a clear and concise response with well-organized ideas.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Use of evidence: Integrates specific and relevant evidence seamlessly to support the response.',
            },
          ],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [{ text: '' }],
        },
        {
          type: 'paragraph',
          align: 'center',
          size: 80,
          color: '#282F33',
          children: [
            {
              text:
                'Critical thinking: Demonstrates insightful and nuanced thinking, connecting ideas with depth.',
            },
          ],
        },
      ]),
      role: 'user' as PromptMessageRole,
    };
    const promptOutput: PromptOutput = {
      type: 'string',
      description: 'feedback for student',
      properties: {},
      resource: { type: 'openAi', model: promptModels.gpt4o },
      propertiesOrder: undefined,
    };
    return {
      id: 'prompt',
      name: 'Prompt',
      messages: [systemInstruction, promptInstruction],
      promptOutput,
    };
  }

  // Default to elaShortAnswer
  const systemInstruction = {
    content: JSON.stringify([
      {
        type: 'paragraph',
        children: [
          {
            text: '# Role: An expert ',
          },
          {
            type: 'livePromptInput',
            inputId: 'subject',
            label: 'Subject',
            children: [
              {
                text: '',
              },
            ],
          },
          {
            text: ' teacher ',
          },
        ],
        align: 'center',
        size: 80,
        color: '#282F33',
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'You are giving a student very short and concise feedback on their written work for a given task in an interactive lesson.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        children: [
          {
            text: '## Communication policy.',
          },
        ],
        align: 'center',
        size: 80,
        color: '#282F33',
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '- You always respond in ',
          },
          {
            type: 'livePromptInput',
            inputId: 'language',
            label: 'Language',
            children: [
              {
                text: '',
              },
            ],
          },
          {
            text: '.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '- You always respond to the student with text written at a ',
          },
          {
            type: 'livePromptInput',
            inputId: 'grade',
            label: 'Grade',
            children: [
              {
                text: '',
              },
            ],
          },
          {
            text: ' grade Flesch-Kincaid readability level.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '## The current lesson',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: 'Below is all content provided in the lesson.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '<Lesson>',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
          {
            type: 'livePromptInput',
            inputId: 'lessonContext',
            label: 'Lesson Context',
            children: [
              {
                text: '',
              },
            ],
          },
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '</Lesson>',
          },
        ],
      },
    ]),
    role: 'system' as PromptMessageRole,
  };
  const promptInstruction = {
    content: JSON.stringify([
      {
        type: 'paragraph',
        children: [
          {
            text: 'The student has been working on the task provided on the slide.',
          },
        ],
        align: 'center',
        size: 80,
        color: '#282F33',
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '<Slide>',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
          {
            type: 'livePromptInput',
            inputId: 'slideContext',
            label: 'Slide Context',
            children: [
              {
                text: '',
              },
            ],
          },
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '</Slide>',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: "Here is the students' answer: ",
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '---',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
          {
            type: 'livePromptInput',
            inputId: 'slide-question-answer',
            label: 'Student response',
            children: [
              {
                text: '',
              },
            ],
          },
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '---',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: 'First write something nice, then use this as a rubric:',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'Content: The response demonstrates a clear understanding of the prompt and provides relevant and accurate information.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'Evidence: The response includes specific and appropriate evidence from the text to support the answer.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: 'Organization: The response is well-organized and easy to follow.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'Language: The response uses appropriate language and grammar, and is free of spelling and punctuation errors.',
          },
        ],
      },
    ]),
    role: 'user' as PromptMessageRole,
  };
  const promptOutput: PromptOutput = {
    type: 'string',
    description: 'feedback for student',
    properties: {},
    propertiesOrder: undefined,
    resource: { type: 'openAi', model: promptModels.gpt4o },
  };
  return {
    id: 'prompt',
    name: 'Prompt',
    messages: [systemInstruction, promptInstruction],
    promptOutput,
  };
};
export const createAiMultiModalFeedbackPrompt = (
  _?: AiDrawingFeedbackType, // TODO: Add more types
): Prompt => {
  const systemInstruction = {
    content: JSON.stringify([
      {
        type: 'paragraph',
        children: [
          { text: '# Role: An expert ' },
          {
            type: 'livePromptInput',
            inputId: 'subject',
            label: 'Subject',
            children: [{ text: '' }],
          },
          { text: ' teacher ' },
        ],
        align: 'center',
        size: 80,
        color: '#282F33',
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'You are giving a student very short and concise feedback on their drawing work for a given task in an interactive lesson.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        children: [{ text: '## Communication policy.' }],
        align: 'center',
        size: 80,
        color: '#282F33',
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          { text: '- You always respond in ' },
          {
            type: 'livePromptInput',
            inputId: 'language',
            label: 'Language',
            children: [{ text: '' }],
          },
          { text: '.' },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          { text: '- You always respond to the student with text written at a ' },
          {
            type: 'livePromptInput',
            inputId: 'grade',
            label: 'Grade',
            children: [{ text: '' }],
          },
          { text: ' grade Flesch-Kincaid readability level.' },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '## The current lesson' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: 'Below is all content provided in the lesson.' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '<Lesson>' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          { text: '' },
          {
            type: 'livePromptInput',
            inputId: 'lessonContext',
            label: 'Lesson Context',
            children: [{ text: '' }],
          },
          { text: '' },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '</Lesson>' }],
      },
    ]),
    role: 'system' as PromptMessageRole,
  };
  const promptInstruction = {
    content: JSON.stringify([
      {
        type: 'paragraph',
        children: [
          { text: 'The student has been working on the task provided on the slide.' },
        ],
        align: 'center',
        size: 80,
        color: '#282F33',
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '<Slide>' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          { text: '' },
          {
            type: 'livePromptInput',
            inputId: 'slideContext',
            label: 'Slide Context',
            children: [{ text: '' }],
          },
          { text: '' },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '</Slide>' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: 'First write something nice, then use this as a rubric:' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'Content: The response demonstrates a clear understanding of the prompt and provides relevant and accurate information.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'Evidence: The response includes specific and appropriate evidence from the text to support the answer.',
          },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          { text: 'Organization: The response is well-organized and easy to follow.' },
        ],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        align: 'center',
        size: 80,
        color: '#282F33',
        children: [
          {
            text:
              'Language: The response uses appropriate language and grammar, and is free of spelling and punctuation errors.',
          },
        ],
      },
    ]),
    role: 'user' as PromptMessageRole,
  };
  const promptOutput: PromptOutput = {
    type: 'string',
    description: 'feedback for student',
    properties: {},
    propertiesOrder: undefined,
    resource: { type: 'openAi-multiModal', model: promptModels.gpt4Turbo },
  };
  return {
    id: 'prompt',
    name: 'Prompt',
    messages: [systemInstruction, promptInstruction],
    promptOutput,
  };
};

export const getPromptTemplate = (prompt?: Prompt) => {
  if (!prompt) return;
  if (!prompt.messages) return;
  const allMessages = prompt.messages.map(message => message.content);

  if (
    JSON.stringify(
      createAiFeedbackPrompt('elaShortAnswer').messages.map(m => m.content),
    ) === JSON.stringify(allMessages)
  ) {
    return 'elaShortAnswer';
  }
  if (
    JSON.stringify(
      createAiFeedbackPrompt('scienceShortAnswer').messages.map(m => m.content),
    ) === JSON.stringify(allMessages)
  ) {
    return 'scienceShortAnswer';
  }
  if (
    JSON.stringify(
      createAiFeedbackPrompt('socialStudiesShortAnswer').messages.map(m => m.content),
    ) === JSON.stringify(allMessages)
  ) {
    return 'socialStudiesShortAnswer';
  }
  return 'custom';
};

export const getDrawingPromptTemplate = (prompt?: Prompt) => {
  if (!prompt) return;
  if (!prompt.messages) return;
  const allMessages = prompt.messages.map(message => message.content);

  if (
    JSON.stringify(
      createAiMultiModalFeedbackPrompt('math').messages.map(m => m.content),
    ) === JSON.stringify(allMessages)
  ) {
    return 'math';
  }
  if (
    JSON.stringify(
      createAiMultiModalFeedbackPrompt('science').messages.map(m => m.content),
    ) === JSON.stringify(allMessages)
  ) {
    return 'science';
  }
  if (
    JSON.stringify(
      createAiMultiModalFeedbackPrompt('socialStudies').messages.map(m => m.content),
    ) === JSON.stringify(allMessages)
  ) {
    return 'socialStudies';
  }
  return 'custom';
};
